import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, errMsg, fileUrl, openPdf } from "@finance/lib/api";
import { exportToCsv } from "@finance/lib/exportCsv";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@finance/components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);
const COLS = [{ key: "kind", label: "Type" }, { key: "no", label: "No" }, { key: "party", label: "Party" }, { key: "link_label", label: "Linked PO" },
  { key: "raised_on", label: "Raised on" }, { key: "posting_date", label: "Posting date" }, { key: "created_by", label: "Raised by" },
  { key: "decided_by", label: "Decided by" }, { key: "decided_at", label: "Decided at" }, { key: "reason", label: "Reason / note" }];

function ApproveDialog({ row, onClose, onDone }) {
  const [date, setDate] = useState(today());
  const [reason, setReason] = useState("");
  const [tab, setTab] = useState("doc");
  const [detail, setDetail] = useState(null);
  useEffect(() => { if (row?.batch) api.get(`/dn-batches/${row.id}`).then((r) => setDetail(r.data)).catch(() => {}); }, [row]);
  if (!row) return null;
  const base = row.kind === "GRV" ? "grvs" : row.batch ? "dn-batches" : "delivery-notes";
  const act = async (kind) => {
    try {
      if (kind === "approve") await api.post(`/${base}/${row.id}/authorize`, { date, reason });
      else { const r = window.prompt("Reason for rejecting:"); if (!r) return; await api.post(`/${base}/${row.id}/reject`, { reason: r }); }
      toast.success(kind === "approve" ? `${row.no} approved` : `${row.no} rejected`); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const atts = detail?.attachments || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl" data-testid="approve-dialog">
        <DialogHeader><DialogTitle className="font-display">{row.kind} {row.no} · {row.party}</DialogTitle></DialogHeader>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList><TabsTrigger value="doc" data-testid="approve-tab-doc">Draft document</TabsTrigger><TabsTrigger value="att" data-testid="approve-tab-att">Attachments ({atts.length})</TabsTrigger></TabsList>
              <TabsContent value="doc"><iframe title="draft" className="h-[60vh] w-full rounded-sm border" src={fileUrl(row.pdf)} data-testid="approve-pdf" /></TabsContent>
              <TabsContent value="att">
                {atts.length === 0 && <div className="py-6 text-center text-xs text-gray-400">No attachments on the linked PO(s)</div>}
                {atts.map((a) => <a key={a.id} className="block border-b py-1.5 text-sm text-[#1B4383]" target="_blank" rel="noreferrer" href={fileUrl(`files/${a.storage_path}`)}>{a.original_filename || a.file_name || a.document_type} {a.client_po_id ? "· client PO" : ""}</a>)}
              </TabsContent>
            </Tabs>
          </div>
          <div className="space-y-3 text-sm">
            <div>Raised on <b>{row.raised_on}</b> by {row.created_by}</div>
            <div><Link className="text-[#1B4383] underline" to={row.link}>Open {row.link_label || "PO"}</Link></div>
            <label className="block text-xs">Posting date (defaults to today)<Input type="date" className="mt-1" data-testid="approve-date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} /></label>
            {date !== today() && <label className="block text-xs">Backdate reason *<Input className="mt-1" data-testid="approve-reason" value={reason} onChange={(e) => setReason(e.target.value)} /></label>}
            <div className="flex gap-2"><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-confirm" onClick={() => act("approve")}>Approve</Button>
              <Button variant="outline" className="border-red-300 text-red-600" data-testid="approve-reject" onClick={() => act("reject")}>Reject</Button></div>
            {(row.history || []).length > 0 && <div className="text-xs text-gray-500">{row.history.map((h, i) => <div key={i}>{h.action} · {h.by} · {fmtDate(h.at)}{h.note ? ` — ${h.note}` : ""}</div>)}</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AuthorizationLog() {
  const [d, setD] = useState({ rows: [], counts: {}, can_authorize: false });
  const [f, setF] = useState({ date_from: "", date_to: "", party: "" });
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/fulfillment/auth-log", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [f]);
  useEffect(() => { load(); }, [load]);
  const quick = async (r, kind) => {
    const base = r.kind === "GRV" ? "grvs" : r.batch ? "dn-batches" : "delivery-notes";
    try {
      if (kind === "approve") await api.post(`/${base}/${r.id}/authorize`, {});
      else { const why = window.prompt("Reason for rejecting:"); if (!why) return; await api.post(`/${base}/${r.id}/reject`, { reason: why }); }
      toast.success("Done"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const table = (status) => {
    const rows = d.rows.filter((r) => r.status === status);
    return (
      <div>
        <div className="mb-2 flex justify-end"><Button size="sm" variant="outline" data-testid={`authlog-export-${status}`} onClick={() => exportToCsv(`authorization-log-${status.toLowerCase()}`, rows, COLS)}>Export CSV</Button></div>
        <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm" data-testid={`authlog-table-${status}`}>
            <thead className="bg-gray-50"><tr className="lx-label border-b text-gray-400">{["Type", "No", "Party", "Linked PO", "Raised on", status === "Pending" ? "Raised by" : "Decided", ""].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-gray-400">Nothing here</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-50" data-testid={`authlog-row-${r.id}`} title={[r.reason, r.decided_at && `${r.decided_by} · ${r.decided_at}`].filter(Boolean).join(" — ")}>
                  <td className="px-3 py-2"><span className="rounded-sm bg-gray-100 px-1 text-[10px] font-bold">{r.kind}</span></td>
                  <td className="px-3 py-2 lx-num font-medium">{r.no}</td>
                  <td className="px-3 py-2">{r.party}</td>
                  <td className="px-3 py-2"><Link className="text-[#1B4383] hover:underline" to={r.link}>{r.link_label || "Open"}</Link></td>
                  <td className="px-3 py-2 lx-num">{r.raised_on}</td>
                  <td className="px-3 py-2 text-xs">{status === "Pending" ? r.created_by : `${r.decided_by || ""} · ${fmtDate(r.decided_at)}`}{r.posting_date && r.posting_date !== r.raised_on ? <div className="text-[10px] text-gray-400">posted {r.posting_date}</div> : null}</td>
                  <td className="px-3 py-2 text-right">
                    {status === "Pending" && d.can_authorize ? (
                      <span className="flex justify-end gap-2">
                        <button className="text-xs font-semibold text-[#1B4383]" data-testid={`authlog-review-${r.id}`} onClick={() => setSel(r)}>Review</button>
                        <button className="text-xs font-semibold text-emerald-700" data-testid={`authlog-approve-${r.id}`} onClick={() => quick(r, "approve")}>Approve</button>
                        <button className="text-xs font-semibold text-red-600" data-testid={`authlog-reject-${r.id}`} onClick={() => quick(r, "reject")}>Reject</button>
                      </span>
                    ) : status === "Approved" ? <button className="text-xs text-[#1B4383]" data-testid={`authlog-pdf-${r.id}`} onClick={() => openPdf(r.pdf)}>PDF</button> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  return (
    <Layout>
      <PageHeader title="Delivery Receipt Authorization Log" subtitle="Every DN and GRV with its authorisation status. Nothing posts to the GL or moves stock until Ronick approves it." />
      <div className="p-4 md:p-8" data-testid="authlog-page">
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <label className="text-xs">From<Input type="date" className="mt-1 h-9" data-testid="authlog-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></label>
          <label className="text-xs">To<Input type="date" className="mt-1 h-9" data-testid="authlog-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></label>
          <label className="text-xs">Party<Input className="mt-1 h-9 w-56" data-testid="authlog-party" value={f.party} onChange={(e) => setF({ ...f, party: e.target.value })} /></label>
        </div>
        <Tabs defaultValue="Pending">
          <TabsList>{["Pending", "Approved", "Rejected"].map((s) => <TabsTrigger key={s} value={s} data-testid={`authlog-tab-${s}`}>{s} ({d.counts?.[s] || 0})</TabsTrigger>)}</TabsList>
          {["Pending", "Approved", "Rejected"].map((s) => <TabsContent key={s} value={s} className="mt-4">{table(s)}</TabsContent>)}
        </Tabs>
      </div>
      {sel && <ApproveDialog row={sel} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); }} />}
    </Layout>
  );
}
